import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cssColor } from '../colors';
import { initials } from '../format';
import type { UserWithRole } from '../types';

export interface UserMenuProps {
  user: UserWithRole;
  connected: boolean;
  onOpenPassword: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}

/** Benutzermenü in der Kopfzeile: Passwort ändern, Abmelden. */
export function UserMenu({
  user,
  connected,
  onOpenPassword,
  onLogout,
  loggingOut,
}: UserMenuProps): ReactNode {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const color = cssColor(user.role?.color);
  const roleName = user.role?.displayName ?? user.role?.name ?? 'Keine Rolle';

  return (
    <div className="user-menu" ref={containerRef}>
      <span
        className={connected ? 'conn-dot conn-on' : 'conn-dot conn-off'}
        title={connected ? 'Echtzeit verbunden' : 'Echtzeit getrennt — neuer Versuch läuft'}
      />
      <button
        type="button"
        className="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="user-dot" style={{ backgroundColor: color }} aria-hidden="true">
          {initials(user.displayName)}
        </span>
        <span className="user-menu-name">{user.displayName}</span>
        <span className="user-role-pill" style={{ borderColor: color, color }}>
          {roleName}
        </span>
        <span className="caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="user-menu-pop" role="menu">
          <div className="user-menu-head">
            <div className="user-menu-head-name">{user.displayName}</div>
            <div className="muted small">@{user.username}</div>
            {!user.hasPassword && (
              <div className="muted small">Kein Passwort gesetzt (nur Cloud-Login)</div>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              setOpen(false);
              onOpenPassword();
            }}
          >
            Passwort ändern
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-item-danger"
            disabled={loggingOut}
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            {loggingOut ? 'Abmelden …' : 'Abmelden'}
          </button>
        </div>
      )}
    </div>
  );
}
